import * as React from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState, NotFoundState } from "@bi-studio/shared/components";
import { DashboardView, DashboardFullscreenHeader } from "@/components/dashboards";
import { PageActions, DashboardActionsButton, ItemDetailModal } from "@/components";

import type {
    GridPosition,
} from "@bi-studio/shared/lib";
import {
    getFolderForItem,
} from "@bi-studio/shared/apis";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchDashboardDetails,
    performRenderDashboard,
    refreshDashboardItem,
    setAutoRefreshEnabled,
    resetDashboardState,
    selectDashboardFull,
    RenderedDashboardItem,
} from "@/store/slices/dashboardSlice";



export default function DashboardDetailPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const { id: dashboardId } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();

    const mode = (searchParams.get("mode") as "view" | "edit" | "standalone" | null) || "view";
    const isFullscreen = searchParams.get("fullscreen") === "true";
    const isEmbed = searchParams.get("embed") === "true";
    const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);

    // Redux selectors
    const {
        dashboard,
        renderedItems,
        chartLayouts,
        isLoading,
        isRendering,
        error,
        autoRefreshEnabled,
        lastRefreshDate: lastRefreshDateStr
    } = useAppSelector(selectDashboardFull);

    const lastRefreshDate = React.useMemo(() => 
        lastRefreshDateStr ? new Date(lastRefreshDateStr) : null
    , [lastRefreshDateStr]);

    // Convert Record to Map for compatibility with DashboardView
    const layoutsMap = React.useMemo(() => 
        new Map<string, GridPosition>(Object.entries(chartLayouts))
    , [chartLayouts]);

    const handleLogout = React.useCallback(() => {
        navigate("/login");
    }, [navigate]);

    React.useEffect(() => {
        if (!token || !dashboardId) return;
        dispatch(fetchDashboardDetails({ token, dashboardId }));
        
        return () => {
            dispatch(resetDashboardState());
        };
    }, [token, dashboardId, dispatch]);

    // Auto-render dashboard when in view mode
    React.useEffect(() => {
        if ((mode === "view" || mode === "standalone") && dashboardId && token && dashboard && !isLoading && renderedItems.length === 0) {
            dispatch(performRenderDashboard({ token, dashboardId }));
        }
    }, [mode, dashboardId, token, dashboard, isLoading, renderedItems.length, dispatch]);

    // Auto-refresh logic (5 minutes)
    const silentRefresh = React.useCallback(async () => {
        if (!token || !dashboardId || isRendering || isLoading) return;
        dispatch(performRenderDashboard({ token, dashboardId }));
    }, [token, dashboardId, isRendering, isLoading, dispatch]);

    React.useEffect(() => {
        let interval: NodeJS.Timeout;
        if (autoRefreshEnabled) {
            // Initial call? No, usually we want to wait for the interval. 
            // If user just toggled it, they probably see current data.
            interval = setInterval(silentRefresh, 5 * 60 * 1000); // 5 minutes
        }
        return () => clearInterval(interval);
    }, [autoRefreshEnabled, silentRefresh]);

    // Chart rendering is now handled by DashboardViewLayout component

    const handleItemPageChange = async (itemId: string, page: number) => {
        if (!token || !dashboardId) return;

        // Find the item
        const item = renderedItems.find((i: RenderedDashboardItem) => i.id === itemId);
        if (!item) return;

        // Determine reportId
        let reportId = item.reportId;
        if (!reportId && item.chart) {
            reportId = item.chart.reportId;
        }

        if (!reportId) return;

        dispatch(refreshDashboardItem({ token, itemId, reportId, page }));
    };

    const handleViewDetails = () => {
        setIsDetailsOpen(true);
    };

    const handleRefresh = async () => {
        if (!token || !dashboardId) return;
        dispatch(performRenderDashboard({ token, dashboardId }));
    };

    const handleFullscreenToggle = () => {
        const params = new URLSearchParams(searchParams);
        if (isFullscreen) {
            params.delete("fullscreen");
        } else {
            params.set("fullscreen", "true");
        }
        setSearchParams(params);
    };

    const handleEdit = () => {
        navigate(`/dashboards/${dashboardId}/edit`);
    };


    const handleBack = React.useCallback(async () => {
        if (!token || !dashboardId) {
            navigate(-1);
            return;
        }

        try {
            const folderInfo = await getFolderForItem(token, "dashboard", dashboardId);
            if (folderInfo.folderId) {
                navigate(`/folders/${folderInfo.folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            console.error("Failed to get folder for dashboard:", err);
            // Fallback to browser back if API call fails
            navigate(-1);
        }
    }, [token, dashboardId, navigate]);

    // Chart refs are now handled by DashboardViewLayout component

    if (!isAuthenticated) {
        return null;
    }

    const isReady = !!dashboard;
    const isPageLoading = isLoading || authLoading;

    if (!isReady && isPageLoading) {
        return (
            <AppLayout onLogout={handleLogout} hideHeader={isFullscreen}>
                <LoadingScreen message="Loading dashboard..." />
            </AppLayout>
        );
    }

    if (!dashboard) {
        const fallback = (
            <NotFoundState
                title="Dashboard Not Found"
                message="The dashboard you are looking for does not exist or you don't have permission to view it."
                showAction={!isEmbed}
                actionLabel="Back to Folders"
                onAction={handleBack}
            />
        );
        return isEmbed ? fallback : <AppLayout onLogout={handleLogout}>{fallback}</AppLayout>;
    }

    const renderContent = () => (
        <>
            <div className={isFullscreen || isEmbed ? "h-screen flex flex-col bg-background" : "space-y-4"}>
                {isFullscreen && (
                    <DashboardFullscreenHeader
                        dashboardName={dashboard.name}
                        autoRefreshEnabled={autoRefreshEnabled}
                        handleFullscreenToggle={handleFullscreenToggle}
                        lastRefreshDate={lastRefreshDate}
                    />
                )}
                {(!isFullscreen && mode !== "standalone") && (
                    <PageActions
                        title={dashboard.name}
                        onBack={!isEmbed ? handleBack : undefined}
                    >
                        {!isEmbed && (
                            <DashboardActionsButton
                                dashboardId={dashboardId!}
                                handleRefresh={handleRefresh}
                                isRendering={isRendering}
                                isLoading={isLoading}
                                autoRefreshEnabled={autoRefreshEnabled}
                                setAutoRefreshEnabled={(val) => dispatch(setAutoRefreshEnabled(val))}
                                handleViewDetails={handleViewDetails}
                                handleFullscreenToggle={handleFullscreenToggle}
                                isFullscreen={isFullscreen}
                                onEdit={handleEdit}
                                lastRefreshDate={lastRefreshDate}
                            />
                        )}
                    </PageActions>
                )}


                {error && (
                    <ErrorState title="Error" message={error} showAction={false} />
                )}

                <DashboardView
                    isRendering={isRendering}
                    renderedItems={renderedItems}
                    dashboard={dashboard}
                    chartLayouts={layoutsMap}
                    isFullscreen={isFullscreen}
                    token={token || ""}
                    onItemPageChange={handleItemPageChange}
                />
            </div>

            <ItemDetailModal
                itemId={dashboardId ?? null}
                itemType="dashboard"
                opened={isDetailsOpen}
                onClose={() => setIsDetailsOpen(false)}
            />
        </>
    );

    if (isEmbed) {
        return renderContent();
    }

    return (
        <AppLayout onLogout={handleLogout} hideHeader={isFullscreen}>
            {renderContent()}
        </AppLayout>
    );
}
